<template>
  <div v-if="quote">
    <ValidationObserver :ref="uniqueId" :id="uniqueId" :name="uniqueId">
        <b-card border-variant="primary" header-bg-variant="primary" header-text-variant="white"
                header-class="p-3 font-size-lg font-weight-bold" body-class="pb-3 pt-3">

          <div class="d-flex justify-content-center mb-2" v-if="!isReadOnly">
            <button v-if="!uwPremiumOverride" class="btn btn-md btn-primary" title="Recalculate Quote Premiums" @click.prevent="recalculateQuotePremiums">
              Recalculate Quote Premiums
            </button>

            <button v-if="!uwPremiumOverride" class="ml-5 btn btn-md btn-warning" title="Override Premiums" @click.prevent="confirmUwOverride">
              Override Premiums
            </button>
            <button v-if="uwPremiumOverride" class="ml-5 btn btn-md btn-secondary" title="Cancel Premium Override" @click.prevent="confirmUwOverrideCancel">
              Cancel Premium Override
            </button>
          </div>

          <div class="font-weight-bold text-dark font-size-lg">
            Vehicles and Premiums
          </div>
          <table class="table table-sm table-bordered mt-3">
            <thead>
              <th width="20%" class="text-center">Vehicle Type</th>
              <th width="10%" class="text-center">No. of Vehicles</th>
              <th width="15%" class="text-center">Total Market Value</th>
              <th width="15%" class="text-center">Cover Type</th>
              <th width="15%" class="text-center">Base Premium Per Vehicle</th>
              <th width="15%" class="text-center" v-if="uwPremiumOverride">Base Premium Per Vehicle (UW Override)</th>
              <th width="10%" class="text-center">Rate Per Vehicle %</th>
            </thead>

            <tr v-for="(vehicle, index) in quote.K_VehiclesAndPremiums" :key="index">
              <td style="vertical-align: middle;">
                <b-form-select class="form-control" v-model="vehicle.K_VehicleType"
                  :options="vehicleTypes" text-field="Name" value-field="idObject" size="sm" disabled
                >
                </b-form-select>
              </td>
              <td>
                <input type="number" class="form-control" v-model="vehicle.K_NumberOfVehicles" disabled/>
              </td>
              <td>
                <currency-input class="form-control" v-model="vehicle.K_TotalMarketValue"
                  :allow-negative="false" :distraction-free="false" :precision="0" disabled
                />
              </td>
              <td style="vertical-align: middle;">
                <b-form-select class="form-control" v-model="vehicle.K_TypeOfCover" size="sm" disabled>
                  <option value="Section 1 & 2">Section 1 & 2</option>
                  <option value="Section 2 Only">Section 2 Only</option>
                  <option v-if="isRenewal" value="Static Risk">Static Risk</option>
                </b-form-select>
              </td>
              <td>
                <ValidationProvider rules="required" :name="`K_BasePremiumPerVehicle${index}`" v-slot="{ errors }">
                  <currency-input class="form-control" v-model="vehicle.K_BasePremiumPerVehicle"
                                  :allow-negative="false" :distraction-free="false" :precision="2"
                                  @input="calculateVehiclePremiumTotals" disabled
                  />
                  <span class="text-danger">{{ errors[0] }}</span>
                </ValidationProvider>
              </td>
              <td v-if="uwPremiumOverride">
                <ValidationProvider rules="required" :name="`K_BasePremiumPerVehicleUwOverride${index}`" v-slot="{ errors }">
                  <currency-input class="form-control" v-model="vehicle.K_BasePremiumPerVehicleUwOverride"
                                  :allow-negative="false" :distraction-free="false" :precision="2"
                                  :disabled="isReadOnly"
                  />
                  <span class="text-danger">{{ errors[0] }}</span>
                </ValidationProvider>
              </td>
              <td>
                <ValidationProvider rules="required" :name="`K_RatePerVehicle${index}`" v-slot="{ errors }">
                  <input type="number" min="0" max="100" class="form-control" v-model="vehicle.K_RatePerVehicle"
                         @input="calculateVehiclePremiumTotals" disabled
                  />
                  <span class="text-danger">{{ errors[0] }}</span>
                </ValidationProvider>
              </td>
            </tr>

            <tr>
              <td></td>
              <td style="vertical-align: middle;">
                <strong>Total: <br />{{ quote.K_TotalNumberOfVehicles }}</strong>
              </td>
              <td style="vertical-align: middle;">
                <strong v-if="quote.K_AverageTotalMarketValue">
                  Average:<br />{{ quote.K_AverageTotalMarketValue | currency }}
                </strong>
              </td>
              <td></td>
              <td style="vertical-align: middle;">
                <strong v-if="quote.K_AverageBasePremiumPerVehicle">
                  Average:<br />{{ quote.K_AverageBasePremiumPerVehicle | currency }}
                </strong>
              </td>
              <td v-if="uwPremiumOverride" style="vertical-align: middle;">
                <strong v-if="quote.K_AverageBasePremiumPerVehicleUwOverride">
                  Average:<br />{{ quote.K_AverageBasePremiumPerVehicleUwOverride | currency }}
                </strong>
              </td>
              <td style="vertical-align: middle;">
                <strong v-if="quote.K_AverageRatePerVehicle">
                  Average:<br />{{ quote.K_AverageRatePerVehicle }} %</strong>
              </td>
            </tr>
          </table>
          <br/>

          <div class="font-weight-bold text-dark font-size-lg">
            State Charges
          </div>
          <table class="table table-sm table-bordered mt-2" v-if="quote.K_StateCharges">
            <thead>
              <th width="10%" class="text-center">State</th>
              <th width="20%" class="text-center">Base Premium</th>
              <th width="15%" class="text-center">ESL/FSL</th>
              <th width="15%" class="text-center">Stamp Duty</th>
              <th width="20%" class="text-center">GST</th>
              <th width="20%" class="text-center">Annual Premium</th>
            </thead>

            <tr v-for="(state, index) in quote.K_StateCharges" :key="index">
              <td class="text-center" style="vertical-align: middle;">
                {{ state.K_State }}
              </td>
              <td>
                <currency-input class="form-control" v-model="state.K_BasePremium"
                                :allow-negative="false" :distraction-free="false" :precision="2"
                                @input="calculateStateSummaryTotals" :disabled="isReadOnly || !uwPremiumOverride"
                />
              </td>
              <td>
                <currency-input
                    class="form-control" v-model="state.K_EslFsl"
                    :allow-negative="false" :distraction-free="false" :precision="2"
                    @input="calculateStateSummaryTotals" :disabled="isReadOnly || !uwPremiumOverride"
                />
              </td>
              <td>
                <currency-input class="form-control" v-model="state.K_StampDuty"
                                :allow-negative="false" :distraction-free="false" :precision="2"
                                @input="calculateStateSummaryTotals" :disabled="isReadOnly || !uwPremiumOverride"
                />
              </td>
              <td>
                <currency-input class="form-control" v-model="state.K_Gst"
                                :allow-negative="false" :distraction-free="false" :precision="2"
                                @input="calculateStateSummaryTotals" :disabled="isReadOnly || !uwPremiumOverride"
                />
              </td>
              <td>
                <currency-input class="form-control" v-model="state.K_AnnualPremium"
                                :allow-negative="false" :distraction-free="false" :precision="2"
                                @input="calculateStateSummaryTotals" :disabled="isReadOnly || !uwPremiumOverride"
                />
              </td>
            </tr>
            <tr>
              <td class="text-center" style="vertical-align: middle;"><strong>Total</strong></td>
              <td><strong>{{ quote.stateSummaryTotals && quote.stateSummaryTotals.totalBasePremium | currency }}</strong></td>
              <td><strong>{{ quote.stateSummaryTotals && quote.stateSummaryTotals.totalEslFsl | currency }}</strong></td>
              <td><strong>{{ quote.stateSummaryTotals && quote.stateSummaryTotals.totalStampDuty | currency }}</strong></td>
              <td><strong>{{ quote.stateSummaryTotals && quote.stateSummaryTotals.totalGst | currency }}</strong></td>
              <td><strong>{{ quote.stateSummaryTotals && quote.stateSummaryTotals.totalAnnualPremium | currency }}</strong></td>
            </tr>
          </table>
          <br/>

          <div v-if="experienceRating">
            <div class="font-weight-bold text-dark font-size-lg mb-2">
              Experience Rating - Claims Summary
            </div>
            <table class="table table-sm table-bordered mt-2">
              <tr>
                <td colspan="2"></td>
                <td colspan="4" class="text-center font-weight-bold" style="background-color: lightblue;"><strong>Working Losses</strong></td>
                <td colspan="4" class="text-center font-weight-bold" style="background-color: lightcyan;"><strong>Large Losses</strong></td>
              </tr>
              <tr>
                <th width="10%" class="text-center" style="background-color: lightgray;">Year</th>
                <th width="10%" class="text-center" style="background-color: lightgray;">Fleet Size (Exposure)</th>
                <th width="10%" class="text-center" style="background-color: lightblue;">Claim Number</th>
                <th width="10%" class="text-center" style="background-color: lightblue;">Claim Frequency</th>
                <th width="10%" class="text-center" style="background-color: lightblue;">Average Size</th>
                <th width="10%" class="text-center" style="background-color: lightblue;">Cost Per Risk</th>
                <th width="10%" class="text-center" style="background-color: lightcyan;">Claim Number</th>
                <th width="10%" class="text-center" style="background-color: lightcyan;">Claim Frequency</th>
                <th width="10%" class="text-center" style="background-color: lightcyan;">Average Size</th>
                <th width="10%" class="text-center" style="background-color: lightcyan;">Cost Per Risk</th>
              </tr>

              <tr class="text-center">
                <td style="vertical-align: middle;background-color: lightgray;">Current Year</td>
                <td style="vertical-align: middle;background-color: lightgray;">{{experienceRating.fleetSizeExposureCY}}</td>

                <td style="vertical-align: middle;background-color: lightblue;">{{experienceRating.workingClaimsCountCY}}</td>
                <td style="vertical-align: middle;background-color: lightblue;">{{experienceRating.workingClaimsFreqCY}}%</td>
                <td style="vertical-align: middle;background-color: lightblue;">{{experienceRating.workingClaimsAcsCY | currency}}</td>
                <td style="vertical-align: middle;background-color: lightblue;">{{experienceRating.workingClaimsCprCY | currency}}</td>

                <td style="vertical-align: middle;background-color: lightcyan;">{{experienceRating.largeClaimsCountCY}}</td>
                <td style="vertical-align: middle;background-color: lightcyan;">{{experienceRating.largeClaimsFreqCY}}%</td>
                <td style="vertical-align: middle;background-color: lightcyan;">${{experienceRating.largeClaimsAcsCY}}</td>
                <td style="vertical-align: middle;background-color: lightcyan;">${{experienceRating.largeClaimsCprCY}}</td>
              </tr>

              <tr class="text-center">
                <td style="vertical-align: middle;background-color: lightgray;">1 Year Ago</td>
                <td style="vertical-align: middle;background-color: lightgray;">{{experienceRating.fleetSizeExposure1Y}}</td>

                <td style="vertical-align: middle;background-color: lightblue;">{{experienceRating.workingClaimsCount1Y}}</td>
                <td style="vertical-align: middle;background-color: lightblue;">{{experienceRating.workingClaimsFreq1Y}}%</td>
                <td style="vertical-align: middle;background-color: lightblue;">{{experienceRating.workingClaimsAcs1Y | currency}}</td>
                <td style="vertical-align: middle;background-color: lightblue;">{{experienceRating.workingClaimsCpr1Y | currency}}</td>

                <td style="vertical-align: middle;background-color: lightcyan;">{{experienceRating.largeClaimsCount1Y}}</td>
                <td style="vertical-align: middle;background-color: lightcyan;">{{experienceRating.largeClaimsFreq1Y}}%</td>
                <td style="vertical-align: middle;background-color: lightcyan;">{{experienceRating.largeClaimsAcs1Y | currency}}</td>
                <td style="vertical-align: middle;background-color: lightcyan;">{{experienceRating.largeClaimsCpr1Y | currency}}</td>
              </tr>

              <tr class="text-center">
                <td style="vertical-align: middle;background-color: lightgray;">2 Years Ago</td>
                <td style="vertical-align: middle;background-color: lightgray;">{{experienceRating.fleetSizeExposure2Y}}</td>

                <td style="vertical-align: middle;background-color: lightblue;">{{experienceRating.workingClaimsCount2Y}}</td>
                <td style="vertical-align: middle;background-color: lightblue;">{{experienceRating.workingClaimsFreq2Y}}%</td>
                <td style="vertical-align: middle;background-color: lightblue;">{{experienceRating.workingClaimsAcs2Y | currency}}</td>
                <td style="vertical-align: middle;background-color: lightblue;">{{experienceRating.workingClaimsCpr2Y | currency}}</td>

                <td style="vertical-align: middle;background-color: lightcyan;">{{experienceRating.largeClaimsCount2Y}}</td>
                <td style="vertical-align: middle;background-color: lightcyan;">{{experienceRating.largeClaimsFreq2Y}}%</td>
                <td style="vertical-align: middle;background-color: lightcyan;">{{experienceRating.largeClaimsAcs2Y | currency}}</td>
                <td style="vertical-align: middle;background-color: lightcyan;">{{experienceRating.largeClaimsCpr2Y | currency}}</td>
              </tr>

              <tr class="text-center">
                <td style="vertical-align: middle;background-color: lightgray;">3 Years Ago</td>
                <td style="vertical-align: middle;background-color: lightgray;">{{experienceRating.fleetSizeExposure3Y}}</td>

                <td style="vertical-align: middle;background-color: lightblue;">{{experienceRating.workingClaimsCount3Y}}</td>
                <td style="vertical-align: middle;background-color: lightblue;">{{experienceRating.workingClaimsFreq3Y}}%</td>
                <td style="vertical-align: middle;background-color: lightblue;">{{experienceRating.workingClaimsAcs3Y | currency}}</td>
                <td style="vertical-align: middle;background-color: lightblue;">{{experienceRating.workingClaimsCpr3Y | currency}}</td>

                <td style="vertical-align: middle;background-color: lightcyan;">{{experienceRating.largeClaimsCount3Y}}</td>
                <td style="vertical-align: middle;background-color: lightcyan;">{{experienceRating.largeClaimsFreq3Y}}%</td>
                <td style="vertical-align: middle;background-color: lightcyan;">{{experienceRating.largeClaimsAcs3Y | currency}}</td>
                <td style="vertical-align: middle;background-color: lightcyan;">{{experienceRating.largeClaimsCpr3Y | currency}}</td>
              </tr>

              <tr class="text-center">
                <td style="vertical-align: middle;background-color: lightgray;">4 Years Ago</td>
                <td style="vertical-align: middle;background-color: lightgray;">{{experienceRating.fleetSizeExposure4Y}}</td>

                <td style="vertical-align: middle;background-color: lightblue;">{{experienceRating.workingClaimsCount4Y}}</td>
                <td style="vertical-align: middle;background-color: lightblue;">{{experienceRating.workingClaimsFreq4Y}}%</td>
                <td style="vertical-align: middle;background-color: lightblue;">{{experienceRating.workingClaimsAcs4Y | currency}}</td>
                <td style="vertical-align: middle;background-color: lightblue;">{{experienceRating.workingClaimsCpr4Y | currency}}</td>

                <td style="vertical-align: middle;background-color: lightcyan;">{{experienceRating.largeClaimsCount4Y}}</td>
                <td style="vertical-align: middle;background-color: lightcyan;">{{experienceRating.largeClaimsFreq4Y}}%</td>
                <td style="vertical-align: middle;background-color: lightcyan;">{{experienceRating.largeClaimsAcs4Y | currency}}</td>
                <td style="vertical-align: middle;background-color: lightcyan;">{{experienceRating.largeClaimsCpr4Y | currency}}</td>
              </tr>

              <tr class="text-center">
                <td style="vertical-align: middle;background-color: lightgray;" class="font-weight-boldest">Avg. Weighted</td>
                <td style="vertical-align: middle;background-color: lightgray;" class="font-weight-boldest">{{experienceRating.fleetSizeExposureTotal}}</td>

                <td style="vertical-align: middle;background-color: lightblue;" class="font-weight-boldest">{{experienceRating.workingClaimsCountTotal}}</td>
                <td style="vertical-align: middle;background-color: lightblue;" class="font-weight-boldest">{{experienceRating.workingClaimsFreqAW}}%</td>
                <td style="vertical-align: middle;background-color: lightblue;" class="font-weight-boldest">{{experienceRating.workingClaimsAcsAW | currency}}</td>
                <td style="vertical-align: middle;background-color: lightblue;" class="font-weight-boldest">{{experienceRating.workingClaimsCprAW | currency}}</td>

                <td style="vertical-align: middle;background-color: lightcyan;" class="font-weight-boldest">{{experienceRating.largeClaimsCountTotal}}</td>
                <td style="vertical-align: middle;background-color: lightcyan;" class="font-weight-boldest">{{experienceRating.largeClaimsFreqAW}}%</td>
                <td style="vertical-align: middle;background-color: lightcyan;" class="font-weight-boldest">{{experienceRating.largeClaimsAcsAW | currency}}</td>
                <td style="vertical-align: middle;background-color: lightcyan;" class="font-weight-boldest">{{experienceRating.largeClaimsCprAW | currency}}</td>
              </tr>

              <tr class="text-center">
                <td style="vertical-align: middle;background-color: lightgray;" class="font-weight-boldest">Year Weighted</td>
                <td style="vertical-align: middle;background-color: lightgray;" class="font-weight-boldest"></td>

                <td style="vertical-align: middle;background-color: lightblue;" class="font-weight-boldest"></td>
                <td style="vertical-align: middle;background-color: lightblue;" class="font-weight-boldest">{{experienceRating.workingClaimsFreqYW}}%</td>
                <td style="vertical-align: middle;background-color: lightblue;" class="font-weight-boldest">{{experienceRating.workingClaimsAcsYW | currency}}</td>
                <td style="vertical-align: middle;background-color: lightblue;" class="font-weight-boldest">{{experienceRating.workingClaimsCprYW | currency}}</td>

                <td style="vertical-align: middle;background-color: lightcyan;" class="font-weight-boldest"></td>
                <td style="vertical-align: middle;background-color: lightcyan;" class="font-weight-boldest">{{experienceRating.largeClaimsFreqYW}}%</td>
                <td style="vertical-align: middle;background-color: lightcyan;" class="font-weight-boldest">{{experienceRating.largeClaimsAcsYW | currency}}</td>
                <td style="vertical-align: middle;background-color: lightcyan;" class="font-weight-boldest">{{experienceRating.largeClaimsCprYW | currency}}</td>
              </tr>

              <tr class="text-center">
                <td style="vertical-align: middle;background-color: lightgray;" class="font-weight-boldest">Adopted</td>
                <td colspan="2"></td>

                <td style="vertical-align: middle;background-color: lightblue;" class="font-weight-boldest">{{experienceRating.workingClaimsFreqAdopted}}%</td>
                <td style="vertical-align: middle;background-color: lightblue;" class="font-weight-boldest">{{experienceRating.workingClaimsAcsAdopted | currency}}</td>
                <td style="vertical-align: middle;background-color: lightblue;" class="font-weight-boldest">{{experienceRating.workingClaimsCprAdopted | currency}}</td>

                <td></td>
                <td style="vertical-align: middle;background-color: lightcyan;" class="font-weight-boldest">{{experienceRating.largeClaimsFreqAdopted}}%</td>
                <td style="vertical-align: middle;background-color: lightcyan;" class="font-weight-boldest">{{experienceRating.largeClaimsAcsAdopted | currency}}</td>
                <td style="vertical-align: middle;background-color: lightcyan;" class="font-weight-boldest">{{experienceRating.largeClaimsCprAdopted | currency}}</td>
              </tr>

            </table>
            <br/>
          </div>

          <div class="font-weight-bold text-dark font-size-lg mb-2">
            Premiums
          </div>
          <table class="table table-sm table-bordered mt-2">
            <thead>
              <th width="50%" class="text-center"></th>
              <th width="25%" class="text-center" style=" background-color: lightgray;">Annual Premium</th>
              <th width="25%" class="text-center" style=" background-color: lightgray;">Annual Premium (Per Risk)</th>
            </thead>
            <tr>
              <td class="text-center" style="vertical-align: middle; background-color: lightgray;">
                Experience Technical Premium (Annual)
              </td>
              <td>
                <ValidationProvider rules="" name="K_ExperienceTechnicalPremium" v-slot="{ errors }">
                  <currency-input class="form-control" v-model="quote.K_ExperienceTechnicalPremium"
                                  @input="calculateLossRatioPremium" disabled/>
                  <span class="text-danger">{{ errors[0] }}</span>
                </ValidationProvider>
              </td>
              <td>
                <ValidationProvider rules="" name="K_ExperienceTechnicalPremiumPerRisk" v-slot="{ errors }">
                  <currency-input class="form-control" v-model="quote.K_ExperienceTechnicalPremiumPerRisk" disabled/>
                  <span class="text-danger">{{ errors[0] }}</span>
                </ValidationProvider>
              </td>
            </tr>
            <tr>
              <td class="text-center" style="vertical-align: middle; background-color: lightgray;">
                Exposure Technical Premium (Annual)
              </td>
              <td>
                <ValidationProvider rules="" name="K_ExposureTechnicalPremium" v-slot="{ errors }">
                  <currency-input class="form-control" v-model="quote.K_ExposureTechnicalPremium" disabled/>
                  <span class="text-danger">{{ errors[0] }}</span>
                </ValidationProvider>
              </td>
              <td>
                <ValidationProvider rules="" name="K_ExposureTechnicalPremiumPerRisk" v-slot="{ errors }">
                  <currency-input class="form-control" v-model="quote.K_ExposureTechnicalPremiumPerRisk" disabled/>
                  <span class="text-danger">{{ errors[0] }}</span>
                </ValidationProvider>
              </td>
            </tr>
            <tr>
              <td class="text-center font-weight-bolder" style="vertical-align: middle;">
                Weighting (Experience) - UW Override %
              </td>
              <td>
                <ValidationProvider rules="max_value:100" name="K_ExperienceWeightOverridePercent" v-slot="{ errors }">
                  <div class="input-group">
                    <input type="number" maxlength="5" class="form-control" v-model="quote.K_ExperienceWeightOverridePercent" max="100"
                           :disabled="isReadOnly">
                    <div class="input-group-append"><span class="input-group-text">%</span></div>
                  </div>
                  <span class="text-danger">{{ errors[0] }}</span>
                </ValidationProvider>
              </td>
              <td>
                <span class="font-weight-bolder">UW Override Comments:</span>
                <ValidationProvider rules="max:2000" name="K_UwOverrideComments" v-slot="{ errors }">
                <textarea class="form-control" v-model="quote.K_UwOverrideComments"
                          maxlength="2000" :disabled="isReadOnly"/>
                  <span class="text-danger">{{ errors[0] }}</span>
                </ValidationProvider>
              </td>
            </tr>
            <tr>
              <td class="text-center" style="vertical-align: middle; background-color: lightgray;">
                Weighted Technical Premium (Annual)
              </td>
              <td>
                <ValidationProvider rules="" name="K_WeightedTechnicalPremium" v-slot="{ errors }">
                  <currency-input class="form-control" v-model="quote.K_WeightedTechnicalPremium" disabled/>
                  <span class="text-danger">{{ errors[0] }}</span>
                </ValidationProvider>
              </td>
              <td>
                <ValidationProvider rules="" name="K_WeightedTechnicalPremiumPerRisk" v-slot="{ errors }">
                  <currency-input class="form-control" v-model="quote.K_WeightedTechnicalPremiumPerRisk" disabled/>
                  <span class="text-danger">{{ errors[0] }}</span>
                </ValidationProvider>
              </td>
            </tr>
            <tr>
              <td class="text-center" style="vertical-align: middle; background-color: lightgray;">
                Walkaway Premium
              </td>
              <td>
                <ValidationProvider rules="" name="K_WalkawayPremium" v-slot="{ errors }">
                  <currency-input class="form-control" v-model="quote.K_WalkawayPremium" disabled/>
                  <span class="text-danger">{{ errors[0] }}</span>
                </ValidationProvider>
              </td>
              <td>
                <ValidationProvider rules="" name="K_WalkawayPremiumPerRisk" v-slot="{ errors }">
                  <currency-input class="form-control" v-model="quote.K_WalkawayPremiumPerRisk" disabled/>
                  <span class="text-danger">{{ errors[0] }}</span>
                </ValidationProvider>
              </td>
            </tr>
            <tr>
              <td class="text-center font-weight-bolder" style="vertical-align: middle;">
                Market Adjustment %
                <div class="small font-weight-normal text-muted mt-1">
                  Derived from UW decision base premium
                </div>
              </td>
              <td>
                <ValidationProvider :rules="marketAdjustmentPercentRules" name="K_MarketAdjustmentPercent" v-slot="{ errors }">
                  <div class="input-group">
                    <input type="number" step="any" class="form-control" v-model="quote.K_MarketAdjustmentPercent" :disabled="isReadOnly">
                    <div class="input-group-append"><span class="input-group-text">%</span></div>
                  </div>
                  <span class="text-danger">{{ errors[0] }}</span>
                </ValidationProvider>
                <div class="mt-3" v-if="!isReadOnly && !uwPremiumOverride">
                  <label class="small font-weight-bold mb-1 d-block">UW decision base premium</label>
                  <div class="input-group input-group-sm">
                    <currency-input
                        class="form-control"
                        v-model="uwDecisionBasePremium"
                        :allow-negative="false"
                        :distraction-free="false"
                        :precision="2"
                    />
                    <div class="input-group-append">
                      <button
                        class="btn btn-outline-primary"
                        type="button"
                        title="Calculate Market Adjustment % from Weighted Technical Premium so Base Premium matches the UW decision"
                        @click.prevent="applyUwDecisionBasePremium"
                      >
                        Apply
                      </button>
                    </div>
                  </div>
                  <small class="form-text text-muted">
                    Sets Market Adjustment % as ((decision / Weighted Technical Premium) - 1) × 100, then recalculates. Rated Base Premium must match the decision to the cent.
                  </small>
                  <small v-if="marketAdjustmentLimitPercent === null" class="form-text text-danger">
                    HOLD: market adjustment authority limit is not configured. Non-zero adjustments are refused until UNDERWRITING_MARKET_ADJUSTMENT_LIMIT_PERCENT (or the product rule constant) is set.
                  </small>
                </div>
              </td>
              <td>
                <span class="font-weight-bolder">
                  Market Adjustment Comments:
                  <span v-if="hasMarketAdjustment" class="text-danger">*</span>
                </span>
                <ValidationProvider :rules="marketAdjustmentCommentsRules" name="K_MarketAdjustmentComments" v-slot="{ errors }">
                <textarea class="form-control" v-model="quote.K_MarketAdjustmentComments"
                          maxlength="2000" :disabled="isReadOnly"
                          :placeholder="hasMarketAdjustment ? 'Required — explain why base premium was adjusted (e.g. broker Word quote true-up)' : ''"/>
                  <span class="text-danger">{{ errors[0] }}</span>
                </ValidationProvider>
              </td>
            </tr>
          </table>

          <b-card body-class="p-2">
            <!-- begin: Other Fees Section -->
            <div class="font-weight-bold text-dark font-size-lg mb-2">
              Other Fees
            </div>
            <div class="form-group row mb-1">
              <label class="col-3 col-form-label text-right">Policy Establishment Fee</label>
              <div class="col-3">
                <ValidationProvider rules="required" name="K_PolicyEstablishmentFee" v-slot="{ errors }">
                  <currency-input
                      class="form-control" v-model="quote.K_PolicyEstablishmentFee"
                      :allow-negative="false" :distraction-free="false" :precision="2"
                      @input="calculateTotalFees" :disabled="isReadOnly"
                  />
                  <span class="text-danger">{{ errors[0] }}</span>
                </ValidationProvider>
              </div>
              <label class="col-3 col-form-label text-right">Cancellation Fee</label>
              <div class="col-3">
                <ValidationProvider rules="required" name="K_CancellationFee" v-slot="{ errors }">
                  <currency-input class="form-control" v-model="quote.K_CancellationFee"
                                  :allow-negative="false" :distraction-free="false" :precision="2"
                                  :disabled="isReadOnly"
                  />
                  <span class="text-danger">{{ errors[0] }}</span>
                </ValidationProvider>
              </div>
            </div>
            <div class="form-group row mb-1">
              <label class="col-3 col-form-label text-right">Administration Fee</label>
              <div class="col-3">
                <ValidationProvider rules="required" name="K_AdministrationFees" v-slot="{ errors }">
                  <currency-input
                      class="form-control" v-model="quote.K_AdministrationFees"
                      :allow-negative="false" :distraction-free="false" :precision="2"
                      @input="calculateTotalFees" :disabled="isReadOnly"
                  />
                  <span class="text-danger">{{ errors[0] }}</span>
                </ValidationProvider>
              </div>
              <label v-if="quote.K_PaymentMethod === 'Annual Premium'" class="col-3 col-form-label text-right">
                Endorsement Fee
              </label>
              <div v-if="quote.K_PaymentMethod === 'Annual Premium'" class="col-3">
                <ValidationProvider rules="required" name="K_EndorsementFee" v-slot="{ errors }">
                  <currency-input
                      class="form-control" v-model="quote.K_EndorsementFee"
                      :allow-negative="false" :distraction-free="false" :precision="2"
                      :disabled="isReadOnly"
                  />
                  <span class="text-danger">{{ errors[0] }}</span>
                </ValidationProvider>
              </div>
            </div>
            <div class="form-group row mb-1">
              <label class="col-3 col-form-label text-right" v-if="quote.K_PaymentMethod === 'Monthly Premium'">
                Monthly Fee
              </label>
              <div class="col-3" v-if="quote.K_PaymentMethod === 'Monthly Premium'">
                <ValidationProvider rules="required" name="K_MonthlyFee" v-slot="{ errors }">
                  <currency-input class="form-control" v-model="quote.K_MonthlyFee"
                                  :allow-negative="false" :distraction-free="false" :precision="2"
                                  @input="calculateTotalFees" :disabled="isReadOnly"
                  />
                  <span class="text-danger"><br>{{ errors[0] }}</span>
                </ValidationProvider>
              </div>
            </div>
            <!-- end: Other Fees Section -->

            <!-- begin: Fuse Analytics Fee Section -->
            <div v-if="quote.K_Brand === 'Fuse' || quote.K_Brand === 'Fuse MSI'" class="mb-2">
              <div class="font-weight-bold text-dark font-size-lg mb-1">
                Fuse Analytics Fee
              </div>
              <div class="form-group row mb-1">
                <label class="col-3 col-form-label text-right">Number of Applicable Vehicles</label>
                <div class="col-3">
                  <ValidationProvider rules="required|min_value:0" name="K_NumberOfApplicableVehicles" v-slot="{ errors }">
                    <input class="form-control" type="number" min="0" v-model="quote.K_NumberOfApplicableVehicles"
                           @input="calculateTotalFees" :disabled="isReadOnly"/>
                    <span class="text-danger">{{ errors[0] }}</span>
                  </ValidationProvider>
                </div>
                <label class="col-3 col-form-label text-right">
                  Fee Per Vehicle Per Annum
                </label>
                <div class="col-3">
                  <ValidationProvider rules="required" name="K_FeePVPA" v-slot="{ errors }">
                    <currency-input
                        class="form-control" v-model="quote.K_FeePVPA"
                        :allow-negative="false" :distraction-free="false" :precision="2"
                        @input="calculateTotalFees" :disabled="isReadOnly"
                    />
                    <span class="text-danger">{{ errors[0] }}</span>
                  </ValidationProvider>
                </div>
              </div>
              <div class="form-group row mb-1">
                <label class="col-3 col-form-label text-right">
                  Total Analytics Fee
                </label>
                <div class="col-3">
                  <ValidationProvider rules="required" name="K_TotalAnalyticsFee" v-slot="{ errors }">
                    <currency-input
                        class="form-control" v-model="quote.K_TotalAnalyticsFee"
                        :allow-negative="false" :distraction-free="false" :precision="2"
                        disabled
                    />
                    <span class="text-danger">{{ errors[0] }}</span>
                  </ValidationProvider>
                </div>
              </div>
            </div>
            <!-- end: Fuse Analytics Fee Section -->

            <!-- begin: Other Details Section -->
            <div class="mb-2 font-weight-bold text-dark font-size-lg">
              Other Details
            </div>
            <div class="form-group row mb-2">
              <label class="col-3 col-form-label text-right">Credit Terms (Days)</label>
              <div class="col-3">
                <ValidationProvider rules="required" name="K_CreditTermsDays" v-slot="{ errors }">
                  <b-form-select class="form-control" v-model="quote.K_CreditTermsDays" :disabled="isReadOnly">
                    <option></option>
                    <option value="Seven (7)">Seven (7)</option>
                    <option value="Fourteen (14)">Fourteen (14)</option>
                    <option value="Twenty-one (21)">Twenty-one (21)</option>
                    <option value="Thirty (30)">Thirty (30)</option>
                    <option value="Fourty-five">Fourty-five</option>
                    <option value="Sixty (60)">Sixty (60)</option>
                    <option value="Ninety (90)">Ninety (90)</option>
                  </b-form-select>
                  <span class="text-danger">{{ errors[0] }}</span>
                </ValidationProvider>
              </div>
              <label v-if="quote.K_PaymentMethod !== 'Burning Cost'" class="col-3 col-form-label text-right">Minimum Premium</label>
              <div v-if="quote.K_PaymentMethod !== 'Burning Cost'" class="col-3">
                <currency-input
                    class="form-control" v-model="quote.K_MinimumNonRefundablePremium"
                    :allow-negative="false" :distraction-free="false" :precision="2"
                    :disabled="isReadOnly"
                />
              </div>
            </div>
            <!-- end: Other Details Section -->

            <div v-if="!isReadOnly && showCopyFeesButton">
              <div class="d-flex justify-content-center mb-2">
                <button class="btn btn-sm btn-outline-primary" @click.prevent="copyFeesToOtherQuotes">Copy Fees to Other Quotes</button>
              </div>
              <div v-if="showFeesCopiedText" class="d-flex justify-content-center mb-2">
                <div class="text-success">Fees copied to other quotes ✔</div>
              </div>
            </div>
          </b-card>

          <!-- begin: Estimated Annual Premium Section -->
          <div class="font-weight-bold text-dark font-size-lg mb-2 mt-4">
            Annual Premium
          </div>
          <div class="form-group row mb-1">
            <label class="col-3 col-form-label text-right">Base Premium - 80% Loss Ratio</label>
            <div class="col-3">
              <currency-input
                  class="form-control" v-model="quote.K_BasePremiumLossRatio"
                  :allow-negative="false" :distraction-free="false" :precision="2"
                  :disabled="true"
              />
            </div>
          </div>
          <div class="form-group row mb-1">
            <label class="col-3 col-form-label text-right">Base Premium</label>
            <div class="col-3">
              <ValidationProvider rules="required" name="K_BasePremium" v-slot="{ errors }">
                <currency-input
                    class="form-control" v-model="quote.K_BasePremium"
                    :allow-negative="false" :distraction-free="false" :precision="2"
                    @input="calculatePremiumTotalsUwOverride" :disabled="isReadOnly || !uwPremiumOverride"
                />
                <span class="text-danger">{{ errors[0] }}</span>
              </ValidationProvider>
            </div>
            <label class="col-3 col-form-label text-right">Base Premium Per Risk</label>
            <div class="col-3">
              <ValidationProvider rules="" name="K_BasePremiumPerRisk" v-slot="{ errors }">
                <currency-input
                    class="form-control" v-model="quote.K_BasePremiumPerRisk"
                    :allow-negative="false" :distraction-free="false" :precision="2"
                    :disabled="!uwPremiumOverride"
                />
                <span class="text-danger">{{ errors[0] }}</span>
              </ValidationProvider>
            </div>
          </div>
          <div class="form-group row mb-1">
            <label class="col-3 col-form-label text-right">ESL / FSL</label>
            <div class="col-3">
              <ValidationProvider rules="required" name="K_EslFsl" v-slot="{ errors }">
                <currency-input
                    class="form-control" v-model="quote.K_EslFsl"
                    :allow-negative="false" :distraction-free="false" :precision="2"
                    @input="calculatePremiumTotalsUwOverride" :disabled="isReadOnly || !uwPremiumOverride"
                />
                <span class="text-danger">{{ errors[0] }}</span>
              </ValidationProvider>
            </div>
          </div>
          <div class="form-group row mb-1">
            <label class="col-3 col-form-label text-right">UW GST</label>
            <div class="col-3">
              <ValidationProvider rules="required" name="K_UwGst" v-slot="{ errors }">
                <currency-input
                    class="form-control" v-model="quote.K_UwGst"
                    :allow-negative="false" :distraction-free="false" :precision="2"
                    @input="calculatePremiumTotalsUwOverride"  :disabled="isReadOnly || !uwPremiumOverride"
                />
                <span class="text-danger">{{ errors[0] }}</span>
              </ValidationProvider>
            </div>
          </div>
          <div class="form-group row mb-1">
            <label class="col-3 col-form-label text-right">Stamp Duty</label>
            <div class="col-3">
              <ValidationProvider rules="required" name="K_StampDuty" v-slot="{ errors }">
                <currency-input
                    class="form-control" v-model="quote.K_StampDuty"
                    :allow-negative="false" :distraction-free="false" :precision="2"
                    @input="calculatePremiumTotalsUwOverride" :disabled="isReadOnly || !uwPremiumOverride"
                />
                <span class="text-danger">{{ errors[0] }}</span>
              </ValidationProvider>
            </div>
          </div>
          <div class="form-group row mb-1">
            <label class="col-3 col-form-label text-right">
              <b-badge variant="primary">Sub-Total</b-badge>
            </label>
            <div class="col-3">
              <ValidationProvider rules="required" name="K_SubTotal" v-slot="{ errors }">
                <currency-input
                    class="form-control" v-model="quote.K_SubTotal"
                    :allow-negative="false" :distraction-free="false" :precision="2"
                    disabled
                />
                <span class="text-danger">{{ errors[0] }}</span>
              </ValidationProvider>
            </div>
          </div>
          <div class="form-group row mb-1">
            <label class="col-3 col-form-label text-right">Total Fees</label>
            <div class="col-3">
              <ValidationProvider rules="required" name="K_TotalFees" v-slot="{ errors }">
                <currency-input
                    class="form-control" v-model="quote.K_TotalFees"
                    :allow-negative="false" :distraction-free="false" :precision="2"
                    disabled
                />
                <span class="text-danger">{{ errors[0] }}</span>
              </ValidationProvider>
            </div>
            <label class="col-3 col-form-label text-right">Total Fee GST</label>
            <div class="col-3">
              <ValidationProvider rules="required" name="K_TotalFeeGst" v-slot="{ errors }">
                <currency-input
                    class="form-control" v-model="quote.K_TotalFeeGst"
                    :allow-negative="false" :distraction-free="false" :precision="2"
                    disabled
                />
                <span class="text-danger">{{ errors[0] }}</span>
              </ValidationProvider>
            </div>
          </div>
          <div class="form-group row mb-1">
            <label class="col-3 col-form-label text-right"><b-badge variant="primary">Total Annual Premium</b-badge></label>
            <div class="col-3">
              <ValidationProvider rules="" name="K_TotalAnnualPremium" v-slot="{ errors }">
                <currency-input
                    class="form-control" v-model="quote.K_TotalAnnualPremium"
                    :allow-negative="false" :distraction-free="false" :precision="2"
                    disabled
                />
                <span class="text-danger">{{ errors[0] }}</span>
              </ValidationProvider>
            </div>
          </div>
          <div class="form-group row mb-1">
            <label class="col-3 col-form-label text-right">Broker Commission</label>
            <div class="col-3">
              <ValidationProvider rules="" name="K_BrokerCommission" v-slot="{ errors }">
                <currency-input
                    class="form-control" v-model="quote.K_BrokerCommission"
                    :allow-negative="false" :distraction-free="false" :precision="2"
                    @input="calculatePremiumTotalsUwOverride" :disabled="isReadOnly || !uwPremiumOverride"
                />
                <span class="text-danger">{{ errors[0] }}</span>
              </ValidationProvider>
            </div>
            <label class="col-3 col-form-label text-right">Broker Commission GST</label>
            <div class="col-3">
              <ValidationProvider rules="" name="K_BrokerCommissionGst" v-slot="{ errors }">
                <currency-input
                    class="form-control" v-model="quote.K_BrokerCommissionGst"
                    :allow-negative="false" :distraction-free="false" :precision="2"
                    @input="calculatePremiumTotalsUwOverride" disabled
                />
                <span class="text-danger">{{ errors[0] }}</span>
              </ValidationProvider>
            </div>
          </div>
          <div class="form-group row mb-2">
            <label class="col-3 col-form-label text-right"><b-badge variant="primary">Net Payable to {{ this.quote.K_Brand }}</b-badge></label>
            <div class="col-3">
              <ValidationProvider rules="" name="K_NetPayableToRentsure" v-slot="{ errors }">
                <currency-input
                    class="form-control" v-model="quote.K_NetPayableToRentsure"
                    :allow-negative="false" :distraction-free="false" :precision="2"
                    disabled
                />
                <span class="text-danger">{{ errors[0] }}</span>
              </ValidationProvider>
            </div>
          </div>
          <div class="form-group row mb-1">
            <label class="col-3 col-form-label text-right"><b-badge variant="secondary">Agency Earnings</b-badge></label>
            <div class="col-3">
              <currency-input
                  class="form-control" v-model="quote.K_AgencyEarnings"
                  :allow-negative="false" :distraction-free="false" :precision="2"
                  disabled
              />
            </div>
          </div>
          <!-- end: Estimated Annual Premium Section -->

          <!-- begin: Burning Cost Details Section -->
          <div v-if="quote.K_PaymentMethod === 'Burning Cost'">
            <div class="mt-10 mb-2 font-weight-bold text-dark font-size-lg">
              Burning Cost Details
            </div>
            <div class="form-group row mb-1">
              <label class="col-3 col-form-label text-right">Deposit Premium</label>
              <div class="col-3">
                <ValidationProvider rules="required" name="K_DepositPremium" v-slot="{ errors }">
                  <currency-input
                      class="form-control" v-model="quote.K_DepositPremium"
                      :allow-negative="false" :distraction-free="false" :precision="2"
                      :disabled="isReadOnly"
                  />
                  <span class="text-danger"><br>{{ errors[0] }}</span>
                </ValidationProvider>
              </div>
            </div>
            <div class="form-group row mb-1">
              <label class="col-3 col-form-label text-right">Minimum Premium</label>
              <div class="col-3">
                <ValidationProvider rules="required" name="K_MinimumPremium" v-slot="{ errors }">
                  <currency-input
                      class="form-control" v-model="quote.K_MinimumPremium"
                      :allow-negative="false" :distraction-free="false" :precision="2"
                      :disabled="isReadOnly"
                  />
                  <span class="text-danger"><br>{{ errors[0] }}</span>
                </ValidationProvider>
              </div>
              <label class="col-3 col-form-label text-right">Maximum Premium</label>
              <div class="col-3">
                <ValidationProvider rules="required" name="K_MaximumPremium" v-slot="{ errors }">
                  <currency-input
                      class="form-control" v-model="quote.K_MaximumPremium"
                      :allow-negative="false" :distraction-free="false" :precision="2"
                      :disabled="isReadOnly"
                  />
                  <span class="text-danger"><br>{{ errors[0] }}</span>
                </ValidationProvider>
              </div>
            </div>
          </div>
          <!-- end: Burning Cost Details Section -->
        </b-card>
    </ValidationObserver>
  </div>
</template>

<style lang="scss">
@import "@/assets/sass/pages/wizard/wizard-4";
</style>

<script>
import _ from "lodash";
import Swal from "sweetalert2";

export default {
  name: "quote",

  props: {
    quote: Object,
    vehicleTypes: Array,
    isRenewal: Boolean,
    isPanelExpanded: Boolean,
    isReadOnly: Boolean,
    showCopyFeesButton: Boolean
  },

  data() {
    return {
      showFeesCopiedText: false,
      uwDecisionBasePremium: null
    };
  },

  methods: {
    calculateVehiclePremiumTotals: function() {
      this.$emit('calculateVehiclePremiumTotals', this.quote);
    },

    calculateStateSummaryTotals: function() {
      this.$emit('calculateStateSummaryTotals', this.quote);
    },

    calculateTotalFees: function () {
      this.$emit('calculateTotalFees', this.quote);
    },

    calculateLossRatioPremium: function () {
      let basePremiumLossRatio = null;
      if(this.quote.K_ExperienceTechnicalPremium) {
        basePremiumLossRatio = ((this.quote.K_ExperienceTechnicalPremium / 100 * 68.5)*100/80);
        basePremiumLossRatio = this.round(basePremiumLossRatio);
      }
      this.quote.K_BasePremiumLossRatio = basePremiumLossRatio;
    },

    calculatePremiumTotalsUwOverride: function() {
      if(this.uwPremiumOverride) {
        this.$emit('calculatePremiumTotalsUwOverride', this.quote);
      }
    },

    round: function (number) {
      if(!_.isNil(number)) {
        return Math.round(Number(number) * 100 + Number.EPSILON)/100;
      }
      return null;
    },

    calculateGst: function (currencyValue) {
      if (!_.isNil(currencyValue)) {
        return this.round(Number(currencyValue) * 0.1);
      }
      return null;
    },

    isNonZeroAdjustment: function (value) {
      if (value === null || value === undefined || value === '') {
        return false;
      }
      const number = Number(value);
      return !Number.isNaN(number) && number !== 0;
    },

    assertMarketAdjustmentAuthorityClient: function (marketAdjustmentPercent) {
      const limit = this.marketAdjustmentLimitPercent;
      if (limit === null || limit === undefined) {
        Swal.fire({
          titleText: 'Market adjustment authority HOLD',
          text: 'No formal market adjustment authority limit is configured. Set UNDERWRITING_MARKET_ADJUSTMENT_LIMIT_PERCENT (or the product rule constant) before applying a non-zero adjustment. Non-zero adjustments are refused until configured.',
          icon: 'error',
          confirmButtonText: 'OK'
        });
        return false;
      }
      if (Math.abs(marketAdjustmentPercent) > limit) {
        Swal.fire({
          titleText: 'Adjustment out of authority',
          text: `Calculated market adjustment is ${marketAdjustmentPercent}%, which exceeds the configured ±${limit}% authority limit.`,
          icon: 'error',
          confirmButtonText: 'OK'
        });
        return false;
      }
      return true;
    },

    applyUwDecisionBasePremium: async function () {
      const weightedTechnical = Number(this.quote.K_WeightedTechnicalPremium);
      const decision = Number(this.uwDecisionBasePremium);

      if (!weightedTechnical || weightedTechnical <= 0) {
        Swal.fire({
          titleText: 'Weighted Technical Premium required',
          text: 'Generate / recalculate quote premiums first so Weighted Technical Premium is available.',
          icon: 'warning',
          confirmButtonText: 'OK'
        });
        return;
      }

      if (!decision || decision <= 0) {
        Swal.fire({
          titleText: 'UW decision base premium required',
          text: 'Enter the UW decision base premium to match.',
          icon: 'warning',
          confirmButtonText: 'OK'
        });
        return;
      }

      // Prem_Override applies as: tech_prem = weighted_tech * (1 + pct/100)
      const marketAdjustmentPercent = Number((((decision / weightedTechnical) - 1) * 100).toFixed(8));
      if (!this.assertMarketAdjustmentAuthorityClient(marketAdjustmentPercent)) {
        return;
      }

      this.$set(this.quote, 'K_MarketAdjustmentPercent', marketAdjustmentPercent);
      this.$set(this.quote, 'K_UwDecisionBasePremium', decision);

      if (!_.trim(this.quote.K_MarketAdjustmentComments)) {
        Swal.fire({
          titleText: 'UW note required',
          html: `<p class="text-justify">Market Adjustment % has been set to <b>${marketAdjustmentPercent}%</b> so Base Premium targets the UW decision of <b>${decision}</b>.<br/><br/>Enter a Market Adjustment Comment explaining the decision, then click <b>Recalculate Quote Premiums</b>.</p>`,
          icon: 'info',
          confirmButtonText: 'OK'
        });
        return;
      }

      await this.recalculateQuotePremiums();
    },

    validateForm: async function () {
      const observer = this.$refs[this.uniqueId];
      if (observer && typeof observer.validate === 'function') {
        return await observer.validate();
      }
      return true;
    },

    confirmUwOverride: function () {
      Swal.fire({
        titleText: "Confirm UW Premium Override",
        html: `<p class="text-justify">This action will <b>clear all state-charges and premiums</b>, and you will need to manually calculate and enter the values.<br/><br/> Are you sure you want to override premium values?</p>`,
        icon: "warning",
        allowOutsideClick: false,
        allowEnterKey: false,
        confirmButtonText: 'Yes',
        showConfirmButton: true,
        showCancelButton: true,
        showCloseButton: false
      })
          .then(result => {
            if(result && result.isConfirmed) {
              // Clear state charges if more than 1 state
              const stateCharges = this.quote.K_StateCharges;
              if(Array.isArray(stateCharges) && stateCharges.length > 1) {
                stateCharges.forEach(state => {
                  state.K_BasePremium = null;
                  state.K_EslFsl = null;
                  state.K_StampDuty = null;
                  state.K_Gst = null;
                  state.K_AnnualPremium = null;
                });
                this.calculateStateSummaryTotals();
              }

              // Clear premium values
              this.quote.K_BasePremium = null;
              this.quote.K_BasePremiumPerRisk = null;
              this.quote.K_EslFsl = null;
              this.quote.K_UwGst = null;
              this.quote.K_StampDuty = null;
              this.quote.K_BrokerCommission = null;
              this.quote.K_BrokerCommissionGst = null;
              this.quote.K_SubTotal = null;
              this.quote.K_TotalAnnualPremium = null;
              this.quote.K_NetPayableToRentsure = null;

              this.calculatePremiumTotalsUwOverride();

              // Set the flag
              this.quote.K_UwPremiumOverride = true;
            }
          })
    },

    confirmUwOverrideCancel: function () {
      Swal.fire({
        titleText: "Cancel UW Premium Override",
        html: `<p class="text-justify">Are you sure you want to cancel the premium override?</p>`,
        icon: "warning",
        allowOutsideClick: false,
        allowEnterKey: false,
        confirmButtonText: 'Yes',
        showConfirmButton: true,
        showCancelButton: true,
        showCloseButton: false
      })
          .then(result => {
            if(result && result.isConfirmed) {
              this.quote.K_UwPremiumOverride = false;

              this.recalculateQuotePremiums();
            }
          })
    },

    recalculateQuotePremiums: async function () {
      const isValid = await this.validateForm();
      if (!isValid) {
        Swal.fire({
          titleText: 'UW note required',
          text: 'Please complete the required underwriting comments before recalculating premiums.',
          icon: 'warning',
          confirmButtonText: 'OK'
        });
        return;
      }
      if (this.hasMarketAdjustment && !this.assertMarketAdjustmentAuthorityClient(Number(this.quote.K_MarketAdjustmentPercent))) {
        return;
      }
      if (this.uwDecisionBasePremium) {
        this.$set(this.quote, 'K_UwDecisionBasePremium', Number(this.uwDecisionBasePremium));
      }
      this.$emit('recalculateQuotePremiums', this.quote);
    },

    copyFeesToOtherQuotes: function () {
      this.$emit('copyFeesToOtherQuotes', this.quote);

      // Show success text for 3 seconds.
      this.showFeesCopiedText = true;
      setTimeout(() => {
        this.showFeesCopiedText = false;
      }, 3000);
    }
  },

  computed: {
    uniqueId: function () {
      return this.quote?.K_UniqueId;
    },

    experienceRating: function () {
      return this.quote?.K_ExperienceRatingOutputJson;
    },

    uwPremiumOverride: function () {
      return this.quote?.K_UwPremiumOverride;
    },

    hasMarketAdjustment: function () {
      return this.isNonZeroAdjustment(this.quote?.K_MarketAdjustmentPercent);
    },

    marketAdjustmentCommentsRules: function () {
      return this.hasMarketAdjustment ? 'required|max:2000' : 'max:2000';
    },

    marketAdjustmentLimitPercent: function () {
      // Server-resolved on quote-rating response. Null = fail-closed until configured.
      const fromServer = this.quote && this.quote.marketAdjustmentLimitPercent;
      if (fromServer === null || fromServer === undefined || fromServer === '') {
        return null;
      }
      const n = Number(fromServer);
      return Number.isFinite(n) && n >= 0 ? n : null;
    },

    marketAdjustmentPercentRules: function () {
      const limit = this.marketAdjustmentLimitPercent;
      if (limit === null || limit === undefined) {
        // Fail-closed: only zero / blank is allowed until a limit is configured.
        return 'max_value:0|min_value:0';
      }
      return `max_value:${limit}|min_value:-${limit}`;
    }
  }

};
</script>
